import React, { DragEvent, useRef, useState } from 'react';
import {
  FaBookOpen,
  FaCheck,
  FaCloudUploadAlt,
  FaComments,
  FaEdit,
  FaFilePdf,
  FaFileUpload,
  FaImages,
  FaPalette,
  FaPen,
  FaRegStar,
  FaSlidersH,
} from 'react-icons/fa';
import { useAuth } from '../auth/AuthContext';
import LanguageSelector from '../languageSelector/LanguageSelector';
import { COMIC_LANGUAGES } from '../comicForm/constants/languages';
import { INPUT_TYPES } from '../comicForm/constants/inputTypes';
import { MIN_COMIC_DESCRIPTION_CHARACTERS } from '../../constants/globalConstants';
import { useLanguage } from '../../hooks/useLanguage';
import { saveLandingDraft } from './landingDraft';
import landingTranslations from './translations';
import LandingFooter from './LandingFooter';
import './LandingPage.css';
import { asIcon } from '../icons';

const UploadIcon = asIcon(FaCloudUploadAlt);
const PdfIcon = asIcon(FaFilePdf);
const PenIcon = asIcon(FaPen);
const CheckIcon = asIcon(FaCheck);
const ResearchIcon = asIcon(FaBookOpen);
const StoryIcon = asIcon(FaComments);
const ComicIcon = asIcon(FaImages);
const SourceStepIcon = asIcon(FaFileUpload);
const SettingsStepIcon = asIcon(FaSlidersH);
const ScriptStepIcon = asIcon(FaEdit);
const DesignStepIcon = asIcon(FaPalette);
const BenefitIcon = asIcon(FaRegStar);

const LandingPage: React.FC = () => {
  const { login, register } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [inputType, setInputType] = useState(INPUT_TYPES.upload);
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [comicLanguage, setComicLanguage] = useState(COMIC_LANGUAGES.Serbian);
  const [isDragging, setIsDragging] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);
  const { translations, language, changeLanguage } =
    useLanguage(landingTranslations);

  const isUpload = inputType === INPUT_TYPES.upload;
  const canContinue = isUpload
    ? Boolean(file)
    : description.trim().length >= MIN_COMIC_DESCRIPTION_CHARACTERS;

  const selectFile = (selectedFile?: File) => {
    if (selectedFile?.type === 'application/pdf') setFile(selectedFile);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    selectFile(event.dataTransfer.files[0]);
  };

  const handleContinue = async () => {
    if (!canContinue || isContinuing) return;

    setIsContinuing(true);
    try {
      await saveLandingDraft({ inputType, description, file, comicLanguage });
      login(window.location.origin + '/comic');
    } catch (error) {
      console.error('Could not save landing page draft:', error);
      setIsContinuing(false);
    }
  };

  return (
    <main className="landing-page">
      <header className="landing-header">
        <a className="landing-wordmark" href="/" aria-label="SciComic home">
          S<span>C</span>i<span>o</span>mic
        </a>
        <nav className="landing-auth-actions" aria-label="Account">
          <div className="landing-language">
            <LanguageSelector
              language={language}
              changeLanguage={changeLanguage}
            />
          </div>
          <button className="landing-register" onClick={() => register()}>
            {translations.register}
          </button>
          <button className="landing-login" onClick={() => login()}>
            {translations.login}
          </button>
        </nav>
      </header>

      <section className="landing-hero">
        <div className="landing-heading">
          <h1>
            {translations.titleFirst}
            <strong>{translations.titleSecond}</strong>
          </h1>
          <p>
            <span>{translations.subtitleFirst}</span>
            <span>{translations.subtitleSecond}</span>
          </p>
        </div>

        <div className="landing-generator" aria-label="Start creating a comic">
          <div className="landing-progress" aria-label="Step 1 of 4">
            <span className="active">
              <CheckIcon size={9} />
            </span>
            <i />
            <span />
            <i />
            <span />
            <i />
            <span />
          </div>
          <h2>{translations.source}</h2>
          <p className="landing-source-description">
            {translations.sourceDescription}
          </p>

          <div className="landing-source-controls">
            <p>{translations.sourceQuestion}</p>
            <div className="landing-source-tabs">
              <button
                type="button"
                className={isUpload ? 'selected' : ''}
                onClick={() => setInputType(INPUT_TYPES.upload)}
              >
                <UploadIcon size={14} /> {translations.uploadPdf}
              </button>
              <button
                type="button"
                className={!isUpload ? 'selected' : ''}
                onClick={() => setInputType(INPUT_TYPES.description)}
              >
                <PenIcon size={13} /> {translations.describeTopic}
              </button>
            </div>
          </div>

          {isUpload ? (
            <div
              className={`landing-dropzone ${isDragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
              onDragEnter={() => setIsDragging(true)}
              onDragLeave={() => setIsDragging(false)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  fileInputRef.current?.click();
                }
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => selectFile(event.target.files?.[0])}
              />
              {file ? (
                <PdfIcon className="landing-file-icon" />
              ) : (
                <UploadIcon />
              )}
              {file ? (
                <span className="landing-file-name">{file.name}</span>
              ) : (
                <span>
                  {translations.dropFile} <u>{translations.browse}</u>
                </span>
              )}
            </div>
          ) : (
            <textarea
              className="landing-topic"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={translations.topicPlaceholder}
              aria-label="Describe the topic"
            />
          )}

          <div className="landing-source-settings">
            <div className="landing-field">
              <label>{translations.language}</label>
              <select
                value={comicLanguage}
                onChange={(event) => setComicLanguage(event.target.value)}
              >
                <option value={COMIC_LANGUAGES.Serbian}>
                  {translations.languageSerbian}
                </option>
                <option value={COMIC_LANGUAGES.Slovenian}>
                  {translations.languageSlovenian}
                </option>
                <option value={COMIC_LANGUAGES.English}>
                  {translations.languageEnglish}
                </option>
                <option value={COMIC_LANGUAGES.Spanish}>
                  {translations.languageSpanish}
                </option>
                <option value={COMIC_LANGUAGES.French}>
                  {translations.languageFrench}
                </option>
                <option value={COMIC_LANGUAGES.Russian}>
                  {translations.languageRussian}
                </option>
                <option value={COMIC_LANGUAGES.Italian}>
                  {translations.languageItalian}
                </option>
                <option value={COMIC_LANGUAGES.Chinese}>
                  {translations.languageChinese}
                </option>
                <option value={COMIC_LANGUAGES.Turkish}>
                  {translations.languageTurkish}
                </option>
                <option value={COMIC_LANGUAGES.Hungarian}>
                  {translations.languageHungarian}
                </option>
                <option value={COMIC_LANGUAGES.Portuguese}>
                  {translations.languagePortuguese}
                </option>
              </select>
            </div>

            <div className="landing-field">
              <label>{translations.comicType}</label>
              <div className="landing-comic-type-card" aria-current="true">
                <div className="landing-template-preview" aria-hidden="true">
                  <div className="landing-template-title" />
                  {[0, 1, 2, 3].map((panelIndex) => (
                    <div className="landing-template-panel" key={panelIndex}>
                      <span className="landing-template-image" />
                      <span className="landing-template-text" />
                    </div>
                  ))}
                </div>
                <div className="landing-comic-type-copy">
                  <strong>{translations.classicFourPanelTemplate}</strong>
                  <span>{translations.onePage}</span>
                  <p>{translations.classicFourPanelTemplateDescription}</p>
                  <ul>
                    <li>{translations.templateTitlePlacement}</li>
                    <li>{translations.templateImagePlacement}</li>
                    <li>{translations.templateTextPlacement}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="landing-continue-row">
            <button
              type="button"
              className="landing-continue"
              disabled={!canContinue || isContinuing}
              onClick={handleContinue}
            >
              {isContinuing ? translations.openingLogin : translations.continue}
            </button>
          </div>
        </div>
      </section>

      <section className="landing-capabilities">
        <h2>{translations.capabilitiesTitle}</h2>
        <div className="landing-capability-list">
          <article>
            <span className="landing-capability-icon">
              <ResearchIcon />
            </span>
            <h3>{translations.researchTitle}</h3>
            <p>{translations.researchDescription}</p>
          </article>
          <article>
            <span className="landing-capability-icon">
              <StoryIcon />
            </span>
            <h3>{translations.storyTitle}</h3>
            <p>{translations.storyDescription}</p>
          </article>
          <article>
            <span className="landing-capability-icon">
              <ComicIcon />
            </span>
            <h3>{translations.comicTitle}</h3>
            <p>{translations.comicDescription}</p>
          </article>
        </div>
      </section>

      <section className="landing-how">
        <h2>{translations.howTitle}</h2>
        <div className="landing-steps">
          <article>
            <span className="landing-step-icon">
              <SourceStepIcon />
            </span>
            <span className="landing-step-number">1</span>
            <h3>{translations.sourceStepTitle}</h3>
            <p>{translations.sourceStepDescription}</p>
          </article>
          <i aria-hidden="true" />
          <article>
            <span className="landing-step-icon">
              <SettingsStepIcon />
            </span>
            <span className="landing-step-number">2</span>
            <h3>{translations.settingsStepTitle}</h3>
            <p>{translations.settingsStepDescription}</p>
          </article>
          <i aria-hidden="true" />
          <article>
            <span className="landing-step-icon">
              <ScriptStepIcon />
            </span>
            <span className="landing-step-number">3</span>
            <h3>{translations.scriptStepTitle}</h3>
            <p>{translations.scriptStepDescription}</p>
          </article>
          <i aria-hidden="true" />
          <article>
            <span className="landing-step-icon">
              <DesignStepIcon />
            </span>
            <span className="landing-step-number">4</span>
            <h3>{translations.designStepTitle}</h3>
            <p>{translations.designStepDescription}</p>
          </article>
        </div>
      </section>

      <section className="landing-offers">
        <h2>{translations.offersTitle}</h2>
        <div className="landing-offer-grid">
          <article className="landing-offer-copy">
            <h3>{translations.studentsTitle}</h3>
            <p>{translations.studentsDescription}</p>
            <ul>
              <li>
                <BenefitIcon /> {translations.studentsBenefitOne}
              </li>
              <li>
                <BenefitIcon /> {translations.studentsBenefitTwo}
              </li>
              <li>
                <BenefitIcon /> {translations.studentsBenefitThree}
              </li>
            </ul>
            <button type="button" onClick={() => register()}>
              {translations.getStarted}
            </button>
          </article>

          <img
            src="/student-research.png"
            alt="Student researching with scientific books"
            className="landing-offer-image"
          />

          <img
            src="/university-classroom.png"
            alt="University lecturer speaking with students"
            className="landing-offer-image"
          />

          <article className="landing-offer-copy">
            <h3>{translations.universitiesTitle}</h3>
            <p>{translations.universitiesDescription}</p>
            <ul>
              <li>
                <BenefitIcon /> {translations.universitiesBenefitOne}
              </li>
              <li>
                <BenefitIcon /> {translations.universitiesBenefitTwo}
              </li>
              <li>
                <BenefitIcon /> {translations.universitiesBenefitThree}
              </li>
            </ul>
            <a href="mailto:info@scaientist.com?subject=SciComic%20for%20universities">
              {translations.contactUs}
            </a>
          </article>
        </div>
      </section>

      <LandingFooter translations={translations} />
    </main>
  );
};

export default LandingPage;
